import {
    type AdminAgentArtifact,
    ArtifactEvidence,
    ArtifactFrame,
    ArtifactLifecycleStatusBadge,
} from '@apps/web/routes/_app/admin/agent/artifacts/frame';
import {
    type AdminArtifactRendererProps,
    formatTokenMetric,
    isRecord,
    JsonValue,
    renderUntrustedString,
    toRecord,
} from '@apps/web/routes/_app/admin/agent/artifacts/shared';
import {
    getTraceAnnotations,
    TraceAnnotationList,
} from '@apps/web/routes/_app/admin/agent/artifacts/trace_timeline';

const MAX_TOKEN_BREAKDOWN_ROWS = 8;

export type TokenBreakdownTotals = {
    readonly totalTokens: number | null;
    readonly inputTokens: number | null;
    readonly outputTokens: number | null;
    readonly cachedReadTokens: number | null;
    readonly cachedCreationTokens: number | null;
    readonly llmCallCount: number | null;
    readonly costUsd: string | null;
};

export type TokenBreakdownRun = {
    readonly runId: string | null;
    readonly traceId: string | null;
    readonly organizationId: string | null;
    readonly model: string | null;
    readonly totalTokens: number | null;
    readonly inputTokens: number | null;
    readonly outputTokens: number | null;
    readonly source: string | null;
};

export function TokenBreakdownArtifact({
    artifact,
}: AdminArtifactRendererProps) {
    const totals = getTokenBreakdownTotals(artifact.payload);
    const metrics = [
        ['Total tokens', formatTokenMetric(totals.totalTokens)],
        ['Input', formatTokenMetric(totals.inputTokens)],
        ['Output', formatTokenMetric(totals.outputTokens)],
        [
            'Cached',
            formatCacheMetric(
                totals.cachedReadTokens,
                totals.cachedCreationTokens,
            ),
        ],
        ['LLM calls', formatTokenMetric(totals.llmCallCount)],
        ['Cost', formatCostMetric(totals.costUsd)],
    ] as const;
    const runs = getTokenBreakdownRuns(artifact.payload);
    const annotations = getTraceAnnotations(artifact.payload);
    return (
        <ArtifactFrame
            artifact={artifact}
            status={
                <ArtifactLifecycleStatusBadge
                    lifecycleStatus={artifact.lifecycle_status}
                />
            }
        >
            <ArtifactEvidence artifact={artifact} />
            {hasTokenBreakdownTotals(totals) ? (
                <div className="border-border/40 grid grid-cols-2 gap-x-4 gap-y-2.5 border-t pt-2.5 sm:grid-cols-3 lg:grid-cols-6">
                    {metrics.map(([label, value]) => (
                        <div key={label}>
                            <div className="text-muted-foreground/70 text-[11px]">
                                {label}
                            </div>
                            <div className="text-foreground mt-0.5 text-[13px] font-medium tabular-nums">
                                {value}
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}
            {runs.length > 0 ? (
                <div className="space-y-1">
                    <h5 className="text-foreground text-xs font-medium">
                        Top token runs
                    </h5>
                    <table className="w-full text-left text-xs">
                        <tbody>
                            {runs.map((run, index) => (
                                <TokenBreakdownRunRow
                                    key={tokenBreakdownRunKey(run, index)}
                                    run={run}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
            <TraceAnnotationList annotations={annotations} />
            <details className="group">
                <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs transition-colors">
                    Raw JSON payload
                </summary>
                <div className="border-border/60 mt-1.5 border-l pl-3">
                    <JsonValue value={artifact.payload} />
                </div>
            </details>
        </ArtifactFrame>
    );
}

function TokenBreakdownRunRow({ run }: { readonly run: TokenBreakdownRun }) {
    return (
        <tr className="border-border/40 border-t align-top first:border-t-0">
            <td className="space-y-0.5 py-1.5 pr-3">
                <div className="text-foreground font-mono text-[11px] break-all">
                    {run.runId
                        ? renderUntrustedString(run.runId)
                        : 'Unknown run'}
                </div>
                {run.traceId ? (
                    <div className="text-muted-foreground/70 text-[11px] break-all">
                        trace {renderUntrustedString(run.traceId)}
                    </div>
                ) : null}
                {run.organizationId ? (
                    <div className="text-muted-foreground/70 text-[11px] break-all">
                        org {renderUntrustedString(run.organizationId)}
                    </div>
                ) : null}
            </td>
            <td className="space-y-0.5 py-1.5 pr-3 text-right">
                <div className="text-foreground font-medium tabular-nums">
                    {formatTokenMetric(run.totalTokens)}
                </div>
                {(run.inputTokens ?? run.outputTokens) !== null ? (
                    <div className="text-muted-foreground/70 text-[11px] tabular-nums">
                        {formatTokenMetric(run.inputTokens)} in /{' '}
                        {formatTokenMetric(run.outputTokens)} out
                    </div>
                ) : null}
            </td>
            <td className="space-y-0.5 py-1.5">
                {run.model ? (
                    <div className="text-foreground text-[11px]">
                        {renderUntrustedString(run.model)}
                    </div>
                ) : null}
                {run.source ? (
                    <div className="text-muted-foreground/70 text-[11px]">
                        {renderUntrustedString(run.source)}
                    </div>
                ) : null}
            </td>
        </tr>
    );
}

function getTokenBreakdownTotals(
    payload: AdminAgentArtifact['payload'],
): TokenBreakdownTotals {
    const payloadRecord = toRecord(payload);
    const records = [
        payloadRecord,
        toRecord(payloadRecord['summary']),
        toRecord(payloadRecord['metrics']),
        toRecord(payloadRecord['usage']),
    ];
    const inputTokens = firstNumberAcross(records, [
        'input_tokens',
        'prompt_tokens',
        'total_input_tokens',
    ]);
    const outputTokens = firstNumberAcross(records, [
        'output_tokens',
        'completion_tokens',
        'total_output_tokens',
    ]);
    const explicitTotal = firstNumberAcross(records, [
        'total_tokens',
        'token_count',
        'total_token_count',
    ]);
    return {
        totalTokens: explicitTotal ?? sumNullable(inputTokens, outputTokens),
        inputTokens,
        outputTokens,
        cachedReadTokens: firstNumberAcross(records, [
            'cached_read_tokens',
            'cache_read_tokens',
            'cached_read_input_tokens',
            'total_cached_read_tokens',
        ]),
        cachedCreationTokens: firstNumberAcross(records, [
            'cached_creation_tokens',
            'cache_creation_tokens',
            'cached_creation_input_tokens',
            'total_cached_creation_tokens',
        ]),
        llmCallCount: firstNumberAcross(records, [
            'llm_call_count',
            'llm_calls',
            'call_count',
        ]),
        costUsd: firstStringAcross(records, ['cost_usd', 'total_cost_usd']),
    };
}

function getTokenBreakdownRuns(
    payload: AdminAgentArtifact['payload'],
): ReadonlyArray<TokenBreakdownRun> {
    const payloadRecord = toRecord(payload);
    const logfireRecord = toRecord(payloadRecord['logfire']);
    const candidates = [
        payloadRecord['top_token_runs'],
        payloadRecord['runs'],
        payloadRecord['rows'],
        logfireRecord['top_token_runs'],
        logfireRecord['runs'],
    ];
    for (const candidate of candidates) {
        if (!Array.isArray(candidate)) {
            continue;
        }
        const runs = candidate.filter(isRecord).flatMap(tokenRunFromRecord);
        if (runs.length > 0) {
            return runs.slice(0, MAX_TOKEN_BREAKDOWN_ROWS);
        }
    }
    return [];
}

function tokenRunFromRecord(
    record: Record<string, unknown>,
): ReadonlyArray<TokenBreakdownRun> {
    const inputTokens = firstNumberValue(record, [
        'input_tokens',
        'prompt_tokens',
        'total_input_tokens',
    ]);
    const outputTokens = firstNumberValue(record, [
        'output_tokens',
        'completion_tokens',
        'total_output_tokens',
    ]);
    const totalTokens =
        firstNumberValue(record, [
            'total_tokens',
            'tokens',
            'token_count',
            'total_token_count',
        ]) ?? sumNullable(inputTokens, outputTokens);
    const runId = firstStringValue(record, [
        'run_id',
        'phoebe_agent_run_id',
        'agent_run_id',
        'id',
    ]);
    const traceId = firstStringValue(record, ['trace_id', 'trace']);
    const model = firstStringValue(record, ['model', 'model_name']);
    if (!runId && !traceId && !model && totalTokens === null) {
        return [];
    }
    return [
        {
            runId,
            traceId,
            organizationId: firstStringValue(record, [
                'organization_id',
                'org_id',
                'organization',
            ]),
            model,
            totalTokens,
            inputTokens,
            outputTokens,
            source: firstStringValue(record, [
                'source',
                'source_label',
                'source_kind',
            ]),
        },
    ];
}

function tokenBreakdownRunKey(run: TokenBreakdownRun, index: number): string {
    return `${index}-${run.runId ?? run.traceId ?? run.model ?? 'token-run'}`;
}

function hasTokenBreakdownTotals(totals: TokenBreakdownTotals): boolean {
    return (
        totals.totalTokens !== null ||
        totals.inputTokens !== null ||
        totals.outputTokens !== null ||
        totals.cachedReadTokens !== null ||
        totals.cachedCreationTokens !== null ||
        totals.llmCallCount !== null ||
        totals.costUsd !== null
    );
}

function formatCacheMetric(
    cachedReadTokens: number | null,
    cachedCreationTokens: number | null,
): string {
    if (cachedReadTokens === null && cachedCreationTokens === null) {
        return 'Unknown';
    }
    return `${formatTokenMetric(cachedReadTokens)} read / ${formatTokenMetric(
        cachedCreationTokens,
    )} write`;
}

// Exported so InvestigationLedgerArtifact can share the cost formatter.
export function formatCostMetric(value: string | null): string {
    if (value === null) {
        return 'Unknown';
    }
    const amount = Number(value);
    if (!Number.isFinite(amount)) {
        return value;
    }
    if (amount === 0) {
        return '$0.00';
    }
    if (amount < 0.01) {
        return `$${amount.toFixed(4)}`;
    }
    return `$${amount.toFixed(2)}`;
}

// Exported so InvestigationLedgerArtifact can share the duration formatter.
export function formatDurationMetric(value: number | null): string | null {
    if (value === null) {
        return null;
    }
    if (value < 1000) {
        return `${value}ms`;
    }
    return `${(value / 1000).toFixed(1)}s`;
}

function formatOptionalScalarString(value: unknown): string | null {
    if (typeof value === 'string' && value.trim()) {
        return value;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
    }
    return null;
}

function firstNumberAcross(
    records: ReadonlyArray<Record<string, unknown>>,
    keys: ReadonlyArray<string>,
): number | null {
    for (const record of records) {
        const value = firstNumberValue(record, keys);
        if (value !== null) {
            return value;
        }
    }
    return null;
}

function firstNumberValue(
    record: Record<string, unknown>,
    keys: ReadonlyArray<string>,
): number | null {
    for (const key of keys) {
        const value = tokenNumberValue(record[key]);
        if (value !== null) {
            return value;
        }
    }
    return null;
}

// Token payloads can carry comma-formatted counts; the timeline parser's
// numberValue deliberately does not accept those.
function tokenNumberValue(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string' && value.trim()) {
        const parsed = Number(value.replace(/,/g, ''));
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

function firstStringAcross(
    records: ReadonlyArray<Record<string, unknown>>,
    keys: ReadonlyArray<string>,
): string | null {
    for (const record of records) {
        const value = firstStringValue(record, keys);
        if (value !== null) {
            return value;
        }
    }
    return null;
}

function firstStringValue(
    record: Record<string, unknown>,
    keys: ReadonlyArray<string>,
): string | null {
    for (const key of keys) {
        const value = formatOptionalScalarString(record[key]);
        if (value) {
            return value;
        }
    }
    return null;
}

function sumNullable(
    first: number | null,
    second: number | null,
): number | null {
    if (first === null || second === null) {
        return null;
    }
    return first + second;
}
